import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Building2,
  Cog,
  FileText,
  ChevronRight,
  Layers3,
  MapPin,
  Menu,
  Nut,
  Settings2,
  Wrench,
} from "lucide-react";

export const PROJECT_ROADMAP_UNASSIGNED_VALUE = "__unassigned__";
export const PROJECT_ROADMAP_QUERY_KEYS = {
  buildingName: "treeBuildingName",
  floor: "treeFloor",
  location: "treeLocation",
  facilityType: "treeFacilityType",
  facilityName: "treeFacilityName",
  partName: "treePartName",
};
const PROJECT_ROADMAP_EXPANDED_STORAGE_NS =
  "project_roadmap_expanded_v1";
const ROADMAP_LEVELS = [
  {
    selectionKey: "buildingName",
    field: "building_name",
    childrenKey: "floors",
    level: "buildingName",
    labelKey: "projectDetail.roadmap.buildingName",
    defaultLabel: "건물명",
    icon: Building2,
  },
  {
    selectionKey: "floor",
    field: "floor",
    childrenKey: "locations",
    level: "floor",
    labelKey: "projectDetail.roadmap.floor",
    defaultLabel: "층",
    icon: Layers3,
  },
  {
    selectionKey: "location",
    field: "location",
    childrenKey: "facilityTypes",
    level: "location",
    labelKey: "projectDetail.roadmap.location",
    defaultLabel: "위치",
    icon: MapPin,
  },
  {
    selectionKey: "facilityType",
    field: "facility_type",
    childrenKey: "facilityNames",
    level: "facilityType",
    labelKey: "projectDetail.roadmap.facilityType",
    defaultLabel: "설비 구분",
    icon: Wrench,
  },
  {
    selectionKey: "facilityName",
    field: "facility_name",
    childrenKey: "parts",
    level: "facilityName",
    labelKey: "projectDetail.roadmap.facilityName",
    defaultLabel: "설비명",
    icon: Cog,
  },
  { selectionKey: "partName", field: "part_name", childrenKey: null, level: "partName", labelKey: "imageFileIndex.report.partName", defaultLabel: "부품명", icon: Nut },
];

export const PROJECT_ROADMAP_LEVELS_STORAGE_KEY = "project_roadmap_levels_v1";
export function normalizeRoadmapLevels(keys) {
  const levels = ROADMAP_LEVELS.filter((level) => Array.isArray(keys) && keys.includes(level.selectionKey));
  return levels.length ? levels : ROADMAP_LEVELS.filter(level => level.selectionKey !== "facilityType");
}

function readRoadmapLevels() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(PROJECT_ROADMAP_LEVELS_STORAGE_KEY));
    const oldDefault = ["buildingName", "floor", "location", "facilityType", "facilityName"];
    return normalizeRoadmapLevels(Array.isArray(saved) && saved.length === 5 && oldDefault.every(key => saved.includes(key))
      ? ["buildingName", "floor", "location", "facilityName", "partName"] : saved);
  } catch {
    return normalizeRoadmapLevels();
  }
}

export function buildProjectRoadmapSearchParams(
  currentSearch,
  nextSelection = {},
) {
  const nextParams = new URLSearchParams(currentSearch);

  ["category", "index", "value", "treeNodeId"].forEach((key) => nextParams.delete(key));
  Object.values(PROJECT_ROADMAP_QUERY_KEYS).forEach((key) =>
    nextParams.delete(key),
  );

  Object.entries(PROJECT_ROADMAP_QUERY_KEYS).forEach(
    ([selectionKey, queryKey]) => {
      if (nextSelection[selectionKey]) {
        nextParams.set(queryKey, nextSelection[selectionKey]);
      }
    },
  );

  nextParams.set("page", "1");
  return nextParams;
}

export function getProjectRoadmapProjectId(pathname) {
  const segments = String(pathname ?? "")
    .split("/")
    .filter(Boolean);

  if (segments[0] !== "project") return null;
  if (!segments[1] || segments[1] === "create") return null;
  if (segments[1] === "file_upload") return segments[2] || null;
  return segments[1];
}

function normalizeTreeValue(value) {
  const normalized = String(value ?? "").trim();
  return normalized || null;
}

function getNodeKey(value) {
  return value ?? PROJECT_ROADMAP_UNASSIGNED_VALUE;
}

function compareTreeNodes(left, right) {
  if (left.value == null && right.value != null) return 1;
  if (left.value != null && right.value == null) return -1;
  return String(left.value ?? "").localeCompare(
    String(right.value ?? ""),
    "ko",
    {
      numeric: true,
      sensitivity: "base",
    },
  );
}

function getRegisteredNodePaths(project) {
  const nodes = Array.isArray(project?.nodes) ? project.nodes : [];
  const typeFields = { building: "building_name", floor: "floor", location: "location", facility: "facility_name", part: "part_name" };
  const typeDepths = { building: 0, floor: 1, location: 2, facility: 4, part: 5 };
  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  return nodes.flatMap((node) => {
    if (!(node.node_type in typeFields)) return [];
    const path = { depth: typeDepths[node.node_type] };
    const visited = new Set();
    let current = node;
    let childDepth = ROADMAP_LEVELS.length;
    while (current) {
      const depth = typeDepths[current.node_type];
      const value = normalizeTreeValue(current.name);
      if (visited.has(current.id) || depth == null || depth >= childDepth || !value ||
          (project.project_id && current.project_id && current.project_id !== project.project_id)) return [];
      visited.add(current.id);
      path[typeFields[current.node_type]] = value;
      childDepth = depth;
      if (!current.parent_id) break;
      current = nodeById.get(current.parent_id);
      if (!current) return [];
    }
    return [path];
  });
}

function buildTreeNodes(files, levelIndex = 0, levels = ROADMAP_LEVELS, registeredPaths = []) {
  const level = levels[levelIndex];
  if (!level) return [];

  const groupedFiles = new Map();
  files.forEach((file) => {
    const value = normalizeTreeValue(file?.[level.field]);
    const nodeKey = getNodeKey(value);
    if (!groupedFiles.has(nodeKey)) {
      groupedFiles.set(nodeKey, { value, files: [], registeredPaths: [] });
    }
    groupedFiles.get(nodeKey).files.push(file);
  });

  const depth = ROADMAP_LEVELS.indexOf(level);
  registeredPaths.forEach((path) => {
    // A registered building alone must not fabricate a floor, location or equipment.
    if (path.depth < depth || !levels.slice(levelIndex).some((nextLevel) => path[nextLevel.field])) return;
    const value = normalizeTreeValue(path[level.field]);
    const nodeKey = getNodeKey(value);
    if (!groupedFiles.has(nodeKey)) {
      groupedFiles.set(nodeKey, { value, files: [], registeredPaths: [] });
    }
    groupedFiles.get(nodeKey).registeredPaths.push(path);
  });

  return Array.from(groupedFiles.values())
    .map((group) => ({
      value: group.value,
      count: group.files.length,
      ...(level.childrenKey && levels[levelIndex + 1]
        ? {
            [level.childrenKey]: buildTreeNodes(
              group.files,
              levelIndex + 1,
              levels,
              group.registeredPaths,
            ),
          }
        : {}),
    }))
    .sort(compareTreeNodes);
}

export function buildProjectRoadmap(project, levels = ROADMAP_LEVELS) {
  const files = Array.isArray(project?.files) ? project.files : [];

  return {
    projectId: project?.project_id ?? null,
    projectName: normalizeTreeValue(project?.project_name),
    fileCount: files.length,
    buildings: buildTreeNodes(files, 0, levels, getRegisteredNodePaths(project)),
  };
}

export function getProjectRoadmapExpandedStorageKey(projectId) {
  return `${PROJECT_ROADMAP_EXPANDED_STORAGE_NS}:${String(projectId ?? "")}`;
}

function readExpandedPaths(storageKey) {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
    return Array.isArray(parsed)
      ? parsed.filter((value) => typeof value === "string")
      : [];
  } catch {
    return [];
  }
}

function writeExpandedPaths(storageKey, paths) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(paths));
  } catch {
    // localStorage를 사용할 수 없는 환경에서는 현재 렌더 상태만 유지한다.
  }
}

function getTreePathKey(selection, levelIndex, levels) {
  return JSON.stringify(
    levels.slice(0, levelIndex + 1).map(
      (level) => selection[level.selectionKey],
    ),
  );
}

function toQueryValue(value) {
  return value ?? PROJECT_ROADMAP_UNASSIGNED_VALUE;
}

function NodeButton({
  icon: Icon,
  value,
  level,
  active,
  withinSelection,
  onClick,
  expanded,
  hasChildren,
  onToggle,
  toggleLabel,
  useProjectLabelIcon = false,
  actions,
}) {
  return (
    <div className={`projectRoadmap__nodeRow${level === "project" ? ` projectRoadmap__nodeRow--project${active ? " is-active" : ""}` : ""}`}>
      {hasChildren ? (
        <button
          type="button"
          className={`projectRoadmap__toggle ${expanded ? "is-open" : ""}`}
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={toggleLabel}
          title={toggleLabel}
        >
          <ChevronRight aria-hidden="true" />
        </button>
      ) : level !== "project" ? (
        <span className="projectRoadmap__toggleSpacer" aria-hidden="true" />
      ) : null}
      <button
        type="button"
        className={[
          "projectRoadmap__nodeButton",
          `projectRoadmap__nodeButton--${level}`,
          active ? "is-active" : "",
          withinSelection ? "is-withinSelection" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={onClick}
        aria-current={active ? "true" : undefined}
        title={value}
      >
        {useProjectLabelIcon ? (
          <span
            className="projectRoadmap__nodeIcon projectRoadmap__nodeIcon--projectLabel"
            aria-hidden="true"
          />
        ) : (
          <Icon className="projectRoadmap__nodeIcon" aria-hidden="true" />
        )}
        <span className="projectRoadmap__nodeCopy">
          <span className="projectRoadmap__nodeValue">{value}</span>
        </span>
      </button>
      {actions}
    </div>
  );
}

function RoadmapNodeList({
  nodes,
  levels,
  levelIndex,
  selection,
  ancestorSelection,
  expandedPaths,
  onToggle,
  onSelect,
  t,
  unassignedLabel,
}) {
  const level = levels[levelIndex];
  if (!level || nodes.length === 0) return null;

  return (
    <ul className="projectRoadmap__group">
      {nodes.map((node) => {
        const queryValue = toQueryValue(node.value);
        const nextSelection = {
          ...ancestorSelection,
          [level.selectionKey]: queryValue,
        };
        const pathKey = getTreePathKey(nextSelection, levelIndex, levels);
        const childNodes = level.childrenKey
          ? node[level.childrenKey] ?? []
          : [];
        const hasChildren = childNodes.length > 0;
        const expanded = hasChildren && expandedPaths.has(pathKey);
        const parentSelected = Object.entries(ancestorSelection).every(
          ([key, value]) => selection[key] === value,
        );
        const nodeSelected =
          parentSelected && selection[level.selectionKey] === queryValue;
        const hasDeeperSelection = levels.slice(levelIndex + 1).some(
          (nextLevel) => Boolean(selection[nextLevel.selectionKey]),
        );
        const active = nodeSelected && !hasDeeperSelection;
        const toggleLabel = t(
          expanded
            ? "projectDetail.roadmap.collapseNode"
            : "projectDetail.roadmap.expandNode",
          {
            defaultValue: expanded ? "하위 항목 접기" : "하위 항목 펼치기",
            name: node.value ?? unassignedLabel,
          },
        );

        return (
          <li key={pathKey} className="projectRoadmap__branch">
            <NodeButton
              icon={level.icon}
              value={node.value ?? unassignedLabel}
              level={level.level}
              active={active}
              withinSelection={nodeSelected && !active}
              expanded={expanded}
              hasChildren={hasChildren}
              onToggle={() => onToggle(pathKey)}
              toggleLabel={toggleLabel}
              onClick={() => onSelect(nextSelection)}
            />

            {expanded ? (
              <RoadmapNodeList
                nodes={childNodes}
                levels={levels}
                levelIndex={levelIndex + 1}
                selection={selection}
                ancestorSelection={nextSelection}
                expandedPaths={expandedPaths}
                onToggle={onToggle}
                onSelect={onSelect}
                t={t}
                unassignedLabel={unassignedLabel}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export default function ProjectRoadmapPanel({
  projectId,
  project,
  isLoading,
  selection,
  onShowGlobalMenu,
  onShowProjectList,
  onShowProjectSettings,
  onShowProjectReport,
  onShowProjectFiles,
  activePage,
  onSelect,
  t,
}) {
  const [levels, setLevels] = useState(readRoadmapLevels);
  const [draftKeys, setDraftKeys] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsRef = useRef(null);
  const optionsButtonRef = useRef(null);
  const dialogRef = useRef(null);
  const roadmap = useMemo(() => buildProjectRoadmap(project, levels), [project, levels]);
  const storageKey = useMemo(
    () => getProjectRoadmapExpandedStorageKey(projectId ?? roadmap.projectId) +
      (levels.length === ROADMAP_LEVELS.length ? "" : `:${levels.map((level) => level.selectionKey).join(",")}`),
    [projectId, roadmap.projectId, levels],
  );
  const [expandedPaths, setExpandedPaths] = useState(
    () => new Set(readExpandedPaths(storageKey)),
  );

  useEffect(() => {
    setExpandedPaths(new Set(readExpandedPaths(storageKey)));
  }, [storageKey]);

  useEffect(() => {
    const selectedPath = [];
    for (const level of levels) {
      const value = selection[level.selectionKey];
      if (!value) break;
      selectedPath.push(value);
    }
    if (selectedPath.length < 2) return;

    setExpandedPaths((current) => {
      const next = new Set(current);
      selectedPath.slice(0, -1).forEach((_, index) => {
        next.add(JSON.stringify(selectedPath.slice(0, index + 1)));
      });
      writeExpandedPaths(storageKey, Array.from(next));
      return next;
    });
  }, [selection, storageKey, levels]);

  useEffect(() => {
    if (settingsOpen) dialogRef.current?.showModal();
    else dialogRef.current?.close();
  }, [settingsOpen]);

  useEffect(() => {
    if (!optionsOpen) return;
    const closeOutside = (event) => {
      if (!optionsRef.current?.contains(event.target)) setOptionsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOptionsOpen(false);
        optionsButtonRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [optionsOpen]);

  const applyLevels = () => {
    const nextLevels = normalizeRoadmapLevels(draftKeys);
    setLevels(nextLevels);
    try {
      window.localStorage.setItem(PROJECT_ROADMAP_LEVELS_STORAGE_KEY, JSON.stringify(nextLevels.map((level) => level.selectionKey)));
    } catch { /* 저장이 제한된 환경에서도 현재 설정은 유지한다. */ }
    const nextSelection = Object.fromEntries(nextLevels
      .filter((level) => selection[level.selectionKey])
      .map((level) => [level.selectionKey, selection[level.selectionKey]]));
    if (Object.keys(PROJECT_ROADMAP_QUERY_KEYS).some((key) => selection[key] && !nextSelection[key])) onSelect(nextSelection);
    setSettingsOpen(false);
  };
  const settingsLabel = t("projectDetail.roadmap.settings", { defaultValue: "표시 계층 설정" });
  const projectListLabel = t("aside.projectChangeShort", { defaultValue: "프로젝트 목록" });
  const projectManagementLabel = t("aside.projectSettingsShort", { defaultValue: "관리 및 설정" });

  const toggleExpandedPath = useCallback(
    (pathKey) => {
      setExpandedPaths((current) => {
        const next = new Set(current);
        if (next.has(pathKey)) next.delete(pathKey);
        else next.add(pathKey);
        writeExpandedPaths(storageKey, Array.from(next));
        return next;
      });
    },
    [storageKey],
  );
  const unassignedLabel = t("projectDetail.roadmap.unassigned", {
    defaultValue: "미지정",
  });
  const projectName =
    roadmap.projectName ||
    t("projectDetail.roadmap.untitledProject", {
      defaultValue: "이름 없는 프로젝트",
    });
  const globalMenuLabel = t("projectDetail.roadmap.showGlobalMenu", {
    defaultValue: "전체 메뉴 보기",
  });
  const hasSelection = Boolean(
    selection.buildingName ||
      selection.floor ||
      selection.location ||
      selection.facilityType ||
      selection.facilityName || selection.partName,
  );

  return (
    <nav
      className="projectRoadmap projectRoadmap--aside is-open"
      aria-label={t("projectDetail.roadmap.title", {
        defaultValue: "프로젝트 탐색",
      })}
    >
      <div className="projectRoadmap__asideToolbar">
        <button
          type="button"
          onClick={onShowGlobalMenu}
          aria-label={globalMenuLabel}
          title={globalMenuLabel}
        >
          <Menu aria-hidden="true" />
          <span>{globalMenuLabel}</span>
        </button>
        <button
          type="button"
          onClick={onShowProjectList}
          aria-label={projectListLabel}
          title={projectListLabel}
        >
          <img
            className="projectRoadmap__changeProjectIcon"
            src={`${process.env.PUBLIC_URL || ""}/images/change_project_icon.png`}
            alt=""
            aria-hidden="true"
          />
          <span>{projectListLabel}</span>
        </button>
        {onShowProjectSettings && <button type="button" onClick={onShowProjectSettings} aria-current={activePage === "settings" ? "page" : undefined} aria-label={projectManagementLabel} title={projectManagementLabel}>
          <Settings2 aria-hidden="true" /><span>{projectManagementLabel}</span>
        </button>}
      </div>

      <div className="projectRoadmap__scroll">
        <dialog ref={dialogRef} className="projectRoadmap__settingsDialog" aria-label={settingsLabel}
          onCancel={() => setSettingsOpen(false)} onClose={() => { setSettingsOpen(false); optionsButtonRef.current?.focus(); }}>
          <h2>{settingsLabel}</h2>
          <p>
            표시할 계층을 1~6개 선택하세요.
            <br />
            {t("projectDetail.roadmap.settingsGuideOrder", { defaultValue: "선택한 항목은 기존 순서대로 표시됩니다." })}
          </p>
          <fieldset>
            <legend>{t("projectDetail.roadmap.visibleLevels", { defaultValue: "표시할 계층" })}</legend>
            {ROADMAP_LEVELS.map((level) => <label key={level.selectionKey}>
              <input type="checkbox" checked={draftKeys.includes(level.selectionKey)}
                disabled={draftKeys.length === 1 && draftKeys.includes(level.selectionKey)}
                onChange={(event) => setDraftKeys((current) => event.target.checked ? [...current, level.selectionKey] : current.filter((key) => key !== level.selectionKey))} />
              <level.icon aria-hidden="true" />{t(level.labelKey, { defaultValue: level.defaultLabel })}
            </label>)}
          </fieldset>
          <p className="projectRoadmap__settingsPreview">{ROADMAP_LEVELS.filter((level) => draftKeys.includes(level.selectionKey)).map((level) => t(level.labelKey, { defaultValue: level.defaultLabel })).join(" › ")}</p>
          <div className="projectRoadmap__settingsActions">
            <button type="button" onClick={() => setDraftKeys(ROADMAP_LEVELS.map((level) => level.selectionKey))}>{t("projectDetail.roadmap.resetLevels", { defaultValue: "기본값" })}</button>
            <button type="button" onClick={() => setSettingsOpen(false)}>{t("projectDetail.roadmap.cancelSettings", { defaultValue: "취소" })}</button>
            <button type="button" className="is-primary" onClick={applyLevels}>{t("projectDetail.roadmap.applySettings", { defaultValue: "적용" })}</button>
          </div>
        </dialog>
        <ul className="projectRoadmap__tree">
          <li className="projectRoadmap__branch projectRoadmap__branch--root">
            <NodeButton
              useProjectLabelIcon
              value={projectName}
              level="project"
              active={!hasSelection && !activePage}
              withinSelection={hasSelection}
              onClick={() => { setOptionsOpen(false); if (onShowProjectFiles) onShowProjectFiles(); else onSelect({}); }}
              actions={<div className="projectRoadmap__projectOptions" ref={optionsRef}>
                <button type="button" ref={optionsButtonRef} className={`ellipsis ${optionsOpen ? "active" : ""}`}
                  aria-label={t("projectDetail.roadmap.projectOptions", { defaultValue: "프로젝트 옵션" })}
                  title={t("projectDetail.roadmap.projectOptions", { defaultValue: "프로젝트 옵션" })}
                  aria-expanded={optionsOpen}
                  onClick={(event) => { event.stopPropagation(); setOptionsOpen((open) => !open); }} />
                <ul className={`optionBox ${optionsOpen ? "active" : ""}`} hidden={!optionsOpen}>
                  <li><button type="button" aria-haspopup="dialog" onClick={() => {
                    setOptionsOpen(false);
                    setDraftKeys(levels.map((level) => level.selectionKey));
                    setSettingsOpen(true);
                  }}><Settings2 aria-hidden="true" />{settingsLabel}</button></li>
                </ul>
              </div>}
            />

            {onShowProjectReport && <button className="projectRoadmap__reportLink" type="button" onClick={onShowProjectReport} aria-current={activePage === "report" ? "page" : undefined}>
              <FileText size={16} aria-hidden="true" /><span>보고서</span>
            </button>}
            {isLoading ? (
              <p className="projectRoadmap__message">
                {t("projectDetail.roadmap.loading", {
                  defaultValue: "프로젝트 목록을 불러오는 중입니다.",
                })}
              </p>
            ) : roadmap.buildings.length === 0 ? (
              <p className="projectRoadmap__message">
                {t("projectDetail.roadmap.empty", {
                  defaultValue: "표시할 프로젝트 파일이 없습니다.",
                })}
              </p>
            ) : (
              <RoadmapNodeList
                nodes={roadmap.buildings}
                levels={levels}
                levelIndex={0}
                selection={selection}
                ancestorSelection={{}}
                expandedPaths={expandedPaths}
                onToggle={toggleExpandedPath}
                onSelect={onSelect}
                t={t}
                unassignedLabel={unassignedLabel}
              />
            )}
          </li>
        </ul>
      </div>
    </nav>
  );
}
